import React, { useCallback, useEffect, useRef } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { FormattingToolbar, useCreateBlockNote } from '@blocknote/react';
import { Box } from '@mui/material';

import { isEmptyNoteContent, parseNoteToBlocks, serializeBlocks } from './serialization';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Minimal schema: remove code blocks and inline code
const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  table: _table,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const schema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

interface AppointmentNoteEditorProps {
  value: string;
  onChange: (serialized: string) => void;
  onBlur?: () => void;
  minHeight?: number;
}

export const AppointmentNoteEditor: React.FC<AppointmentNoteEditorProps> = ({
  value,
  onChange,
  onBlur,
  minHeight = 80,
}) => {
  const editor = useCreateBlockNote({ schema });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isLocalChangeRef = useRef(false);
  const lastEmittedRef = useRef(value);
  const initializedRef = useRef(false);

  // Initialize editor content (once)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!value) return;
    void (async () => {
      try {
        const blocks = await parseNoteToBlocks(editor, value);
        if (blocks.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          editor.replaceBlocks(editor.document, blocks);
        }
      } catch (err) {
        console.error('Error initializing appointment note editor:', err);
      }
    })();
  }, [editor, value]);

  // Sync external value changes (e.g. parent resets appointment)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (isLocalChangeRef.current) {
      isLocalChangeRef.current = false;
      return;
    }
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;

    void (async () => {
      try {
        const blocks = await parseNoteToBlocks(editor, value || '');
        const target = blocks.length > 0 ? blocks : [{ type: 'paragraph' as const, content: '' }];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        editor.replaceBlocks(editor.document, target);
      } catch (err) {
        console.error('Error syncing appointment note editor:', err);
      }
    })();
  }, [editor, value]);

  // Subscribe to editor changes — emit serialized JSON
  useEffect(() => {
    return editor.onChange(() => {
      const serialized = serializeBlocks(editor.document);
      const normalized = isEmptyNoteContent(serialized) ? '' : serialized;
      isLocalChangeRef.current = true;
      lastEmittedRef.current = normalized;
      onChangeRef.current(normalized);
    });
  }, [editor]);

  // Blur detection via focusout on container
  const containerRef = useRef<HTMLDivElement>(null);
  const handleFocusOut = useCallback(
    (e: FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      if (e.relatedTarget && container.contains(e.relatedTarget as Node)) return;
      onBlur?.();
    },
    [onBlur],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('focusout', handleFocusOut);
    return () => container.removeEventListener('focusout', handleFocusOut);
  }, [handleFocusOut]);

  return (
    <Box
      ref={containerRef}
      onClick={e => e.stopPropagation()}
      sx={{
        minHeight,
        bgcolor: 'background.paper',
        // BlockNote renders editor content before children — use flexbox to put toolbar first
        '& .bn-container': {
          display: 'flex',
          flexDirection: 'column',
        },
        '& .bn-editor': {
          fontSize: '0.875rem',
          padding: '8px 14px',
          minHeight: minHeight - 40,
          order: 1,
        },
      }}
    >
      <BlockNoteView
        editor={editor}
        editable={true}
        theme="light"
        formattingToolbar={false}
        sideMenu={false}
      >
        <Box
          sx={{
            order: 0,
            pt: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '& .bn-formatting-toolbar': {
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
              padding: '2px 4px',
              minHeight: 36,
            },
          }}
        >
          <FormattingToolbar />
        </Box>
      </BlockNoteView>
    </Box>
  );
};
